import DOMPurify from "dompurify";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import { HTML_SANITIZE_OPTS, renderMentions } from "@/lib/comment-sanitize";
import { formatDateTimeShort, formatFileSize } from "@/lib/format";
import type { UserLookup } from "@/hooks/use-users";
import { isImageAttachment, openFreelancerFile, type FreelancerCommentRow } from "@/hooks/use-freelancer-comments";

interface Props {
  comment: FreelancerCommentRow;
  users: UserLookup[];
  /** Tidsbegrensede lenker til bildevedlegg, nøkkel er storage_path. */
  imageUrls?: Map<string, string>;
  canDelete: (c: FreelancerCommentRow) => boolean;
  onDelete: (c: FreelancerCommentRow) => void;
  onReply?: (c: FreelancerCommentRow) => void;
  isReply?: boolean;
}

/** Én kommentar i frilanserloggen. Monday-kommentarer har HTML (sanitert), nye har ren tekst med @-nevninger. */
export function FreelancerCommentItem({ comment: c, users, imageUrls, canDelete, onDelete, onReply, isReply = false }: Props) {
  const authorName = c.author?.full_name ?? c.monday_author_name ?? "Ukjent";
  const html = c.body_html
    ? DOMPurify.sanitize(c.body_html, HTML_SANITIZE_OPTS)
    : renderMentions(c.body, c.mentioned_user_ids ?? [], users, false);

  const thumbs = c.attachments.filter((a) => isImageAttachment(a) && imageUrls?.has(a.storage_path as string));
  const files = c.attachments.filter((a) => !thumbs.includes(a));

  return (
    <div className={isReply ? "ml-11 border-l-2 border-border pl-3" : ""}>
      <div className="flex gap-3">
        <Avatar name={authorName} avatarUrl={c.author?.avatar_url} size={isReply ? "sm" : "md"} className="shrink-0" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-medium">{c.author ? authorName : `Skrevet av ${authorName} i Monday`}</span>
            <span className="text-xs text-muted-foreground">{formatDateTimeShort(c.created_at)}</span>
            {c.monday_update_id && <span className="text-xs text-muted-foreground">· fra Monday</span>}
          </div>
          {(c.body || c.body_html) && (
            <div
              className="prose prose-sm max-w-none break-words text-sm [&_a]:text-primary [&_a]:underline [&_img]:max-h-64"
              // Sanitert: DOMPurify for Monday-HTML, escapeHtml i renderMentions for ren tekst.
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          {thumbs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {thumbs.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => openFreelancerFile(a.storage_path as string)}
                  title={`Åpne ${a.name}`}
                  className="overflow-hidden rounded-md border border-border hover:border-foreground/40"
                >
                  <img src={imageUrls?.get(a.storage_path as string)} alt={a.name} loading="lazy" className="h-24 w-32 object-cover" />
                </button>
              ))}
            </div>
          )}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  disabled={!a.storage_path}
                  onClick={() => a.storage_path && openFreelancerFile(a.storage_path)}
                  title={a.storage_path ? "Åpne (tidsbegrenset lenke)" : "Filen flyttes fra Monday"}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:border-foreground/40 disabled:opacity-60"
                >
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase">{a.file_extension ?? "fil"}</span>
                  <span className="max-w-[14rem] truncate">{a.name}</span>
                  {a.file_size != null && <span className="text-muted-foreground">{formatFileSize(a.file_size)}</span>}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-3 text-xs">
            {onReply && !isReply && (
              <button type="button" className="text-primary hover:underline" onClick={() => onReply(c)}>Svar</button>
            )}
            {canDelete(c) && (
              <button type="button" className="inline-flex items-center gap-1 text-muted-foreground hover:text-destructive" onClick={() => onDelete(c)}>
                <SrIcon name="trash" className="h-3 w-3" />
                Slett
              </button>
            )}
          </div>
        </div>
      </div>
      {c.replies.length > 0 && (
        <div className="mt-3 space-y-3">
          {c.replies.map((r) => (
            <FreelancerCommentItem key={r.id} comment={r} users={users} imageUrls={imageUrls} canDelete={canDelete} onDelete={onDelete} isReply />
          ))}
        </div>
      )}
    </div>
  );
}
